import { useContext, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import TkStepper from "../../components/atoms/TkStepper";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import { TkGetCode } from "../../utils/TkGetCode";
import { FormClassRetire, Prefectures } from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { IAPI_RET004_Response, IAPI_RET005_Request } from "../../type/apiType";
import { httpAPI_RET004, httpAPI_RET005 } from "./api/request";
import { fakeTableList, schema, schemaKiko, steps } from "./parts";
import { API_RET005_BuildReqParams } from "../../utils/buildRequestParams";
import _ from "lodash";

/**
 * SCRR411123 退職手当金受領者の変更手続き（請求者の登録）
 * 退職手当金受領者を変更する際に登録した請求者の内容を確認する。
 * @return SCRR411123
 */
const SCRR411123 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);

  const isUserTypeKiko =
    state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO ||
    state.authInfo?.userType === authorityUserType.userTypeNumber.TAISYOKUSYA;
  const contractorNo = isUserTypeKiko
    ? businessState.common?.contractorNo
    : state.authInfo?.contractorNo;
  const staffNo = location.state?.staffNo;
  const context = businessState.job_41;

  const [stateTableList, setStateTableList] = useState<any>([]);
  const [res, setRes] = useState<IAPI_RET004_Response>({});
  const [open, setOpen] = useState(false);

  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));

  // 退職手当金請求参照API実行
  const handleCallApi = async () => {
    try {
      const res = await httpAPI_RET004({
        applyId: context?.applyId,
        contractorNo: contractorNo,
        staffNo: staffNo,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });

        let resArr = stateFakeTableList.current;

        // 初期表示
        for (let i in res) {
          let targetObj = resArr.find((item) => item.keyword === i)!;

          targetObj && (targetObj.setDefaultValue = res[i as keyof typeof res]);
          targetObj && (targetObj.text = res[i as keyof typeof res]);
        }

        // 請求者住所
        let resObj = {
          prefectures: TkGetCode(Prefectures, res.demanderPrefectureCd),
          city: res.demanderCity ?? "",
          address: res.demanderAddressOthers ?? "",
        };
        resArr.find((item) => item.keyword === "locationGroup")!.valueObject =
          resObj;

        // 退職者名
        let retireeName = resArr.find(
          (item) => item.keyword === "retireeName"
        )!;
        retireeName.showLine = isUserTypeKiko;
        retireeName.contractorNumber =
          (location.state?.staffNo ?? (res.staffNo || "")) +
          ":" +
          (location.state?.staffName ?? (res.retirementStaffName || ""));

        // 請求者氏名（ｶﾅ）
        resArr.find(
          (item) => item.keyword === "demanderNameKana"
        )!.setDefaultValue = res.demanderNameKana;

        // 請求者氏名（全角漢字）
        resArr.find(
          (item) => item.keyword === "demanderName"
        )!.setDefaultValue = res.demanderName;

        // 請求者生年月日
        resArr.find(
          (item) => item.keyword === "demanderBirth"
        )!.setDefaultValue = res.demanderBirth;

        // 請求者住所（郵便番号）
        resArr.find(
          (item) => item.keyword === "demanderZipcode"
        )!.setDefaultValue = res.demanderZipcode;

        // 請求者連絡先
        resArr.find((item) => item.keyword === "tel")!.setDefaultValue =
          res.tel;

        // 請求者メールアドレス（機構連絡用）
        let demanderEmail = resArr.find(
          (item) => item.keyword === "demanderEmail"
        )!;
        demanderEmail.requiredIcon = isUserTypeKiko;
        demanderEmail.setDefaultValue = res.demanderEmail;

        // 請求者メールアドレス（機構連絡用）（確認用）
        let demanderEmailConfirm = resArr.find(
          (item) => item.keyword === "demanderEmailConfirm"
        )!;
        demanderEmailConfirm.requiredIcon = isUserTypeKiko;
        demanderEmailConfirm.setDefaultValue = res.demanderEmail;

        // 代行者メールアドレス
        let agentEmail = resArr.find((item) => item.keyword === "agentEmail")!;
        agentEmail.showLine = !isUserTypeKiko;
        agentEmail.setDefaultValue = res.agentEmail;

        // 代行者メールアドレス（確認用）
        let agentEmailConfirm = resArr.find(
          (item) => item.keyword === "agentEmailConfirm"
        )!;
        agentEmailConfirm.showLine = !isUserTypeKiko;
        agentEmailConfirm.setDefaultValue = res.agentEmail;

        let fileList = res.fileList ?? [];
        if (
          !isUserTypeKiko &&
          !fileList.some((file) => file.formClass === FormClassRetire["委任状"])
        ) {
          fileList.push(
            context?.fileList?.find(
              (file) => file.formClass === FormClassRetire["委任状"]
            )!
          );
          fileList.map((file, index) => (file.serialNo = index + 1));
        }

        setBusinessState({
          ...businessState,
          job_41: {
            ...context,
            ...res,
            fileList,
          },
        });

        setRes(res);

        setStateTableList([...resArr]);
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch {
      navigate("/error");
    }
  };

  // 初期表示
  useEffect(() => {
    handleCallApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const makeHttpObj = (obj: any): IAPI_RET005_Request => {
    const reqObj = {
      ...context,
      ...obj,
      tempSaveStep: 5, // 一時保存ステップ
      businessType: context?.businessType,
      contractorNo: businessState.common?.contractorNo, // 共済契約者番号
      tel: res.tel, // 電話番号
      contractorName: businessState.common?.contractorName, // 契約者（法人）名
      screenId: "SCRR411123", // 画面ID
    };
    return API_RET005_BuildReqParams(reqObj);
  };

  const handleSubmit = async (data: any, index: any) => {
    // 業務チェック
    if (
      data.demanderEmail !== data.demanderEmailConfirm ||
      data.agentEmail !== data.agentEmailConfirm
    ) {
      setState({
        ...state,
        errorMsgId: "TKMSG_SCR0001E",
      });
      return;
    }

    const dataObj = {
      demanderName: data.demanderName,
      demanderNameKana: data.demanderNameKana,
      birthday: data.demanderBirth,
      tel: res.tel,
      demanderEmail: data.demanderEmail,
      agentEmail: data.agentEmail,
      demanderZipcode: data.demanderZipcode,
      demanderCity: data.locationGroup?.city,
      demanderAddressOthers: data.locationGroup?.address,
    };

    switch (index) {
      case 0: // 一時保存
        // 退職手当金請求更新API実行
        try {
          const res = await httpAPI_RET005({
            ...makeHttpObj(dataObj),
          });

          if (res.resultCd === "I0000") {
            setBusinessState({
              ...businessState,
              job_41: {
                ...context,
                ...dataObj,
                applyId: res.applyId,
                tempSaveStep: 5,
              },
            });

            setOpen(true);

            setState({
              ...state,
              errorMsgId: "",
            });
          } else {
            setState({
              ...state,
              errorMsgId: res.resultCd,
            });
          }
        } catch (error) {
          navigate("/error");
        }
        break;

      case 1: // 次へ
        setState({
          ...state,
          errorMsgId: "",
        });

        // 持回り情報
        setBusinessState({
          ...businessState,
          job_41: {
            ...context,
            ...dataObj,
          },
        });

        navigate("/SCRR411124");
        break;
    }
  };

  return (
    <Box data-testid="SCRR411123test">
      <TkStepper
        steps={steps}
        activeStep={4}
        savaStep={context?.tempSaveStep}
      />

      <TkFakeTable
        list={stateTableList}
        ruleSchema={isUserTypeKiko ? schemaKiko : schema}
        submitButtonGroup={[
          { submitButtonText: "一時保存" },
          { submitButtonText: "次へ" },
        ]}
        buttonGroup={<TkBackButton />}
        onCheckAndSubmit={(e, index) => handleSubmit(e, index)}
      />
      <TkMessageDialog
        open={open}
        onOky={() => setOpen(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />
    </Box>
  );
};

export default SCRR411123;
